<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.dashboard'),
                        href: meta.links.dashboard_index,
                    },
                    {
                        name: $tc('messages.relatives', 2),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.store" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.name') }}
                    <div class="text-gray-400">
                        {{ full_name }}
                    </div>
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.first_name')" for="first_name" />
                            <FormInput
                                required
                                id="first_name"
                                v-model.trim="form.first_name"
                                name="first_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.first_name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.nickname')" for="name" />
                            <FormInput
                                id="name"
                                v-model.trim="form.name"
                                name="name"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.last_name')" for="last_name" />
                            <FormInput
                                required
                                id="last_name"
                                v-model.trim="form.last_name"
                                name="last_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.last_name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.second_last_name')" for="second_last_name" />
                            <FormInput
                                id="second_last_name"
                                v-model.trim="form.second_last_name"
                                name="second_last_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.second_last_name" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.date_of_birth')" for="date_of_birth" />
                            <FormInput
                                id="date_of_birth"
                                v-model="form.date_of_birth"
                                name="date_of_birth"
                                type="date"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.date_of_birth" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.gender')" for="gender" />
                            <FormSelect
                                required
                                id="gender"
                                v-model="form.gender"
                                :options="options.genders"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.gender" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.mother_tongue')" for="mother_tongue" />
                            <FormSelectFilterablePanel
                                id="mother_tongue"
                                v-model="form.mother_tongue"
                                :options="options.languages"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.mother_tongue" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.nationality')" for="nationality" />
                            <FormSelectFilterablePanel
                                id="nationality"
                                v-model="form.nationality"
                                :options="options.countries"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.nationality" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.country_of_origin')" for="country_of_origin" />
                            <FormSelectFilterablePanel
                                id="country_of_origin"
                                v-model="form.country_of_origin"
                                :options="options.countries"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.country_of_origin" class="mt-2" />
                        </div>
                    </div>

                    <div class="md:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.identification_document')" for="identification_document" />
                            <FormInput
                                id="identification_document"
                                v-model="form.identification_document"
                                name="identification_document"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.identification_document" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.settings') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.preferred_language')" for="preferred_language" />
                            <FormSelect
                                id="preferred_language"
                                v-model="form.preferred_language"
                                :options="options.preferred_languages"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.preferred_language" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.contact_info') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.email')" for="email" />
                            <FormInput
                                required
                                id="email"
                                v-model="form.email"
                                name="email"
                                type="email"
                                max="191"
                                autocomplete="off"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.email" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $tc('messages.phones', 2) }}
                </template>
                <div class="md:grid md:grid-cols-6 md:gap-6" v-for="(phone, index) in form.phones" :key="index">
                    <div class="md:col-span-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $tc('messages.phones', 1) }}
                        </label>
                        <div class="mt-1 flex rounded-md shadow-sm">
                            <span
                                class="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm"
                            >
                                <PhonePrefixSelector v-model="phone.dial_code" />
                            </span>
                            <input
                                type="tel"
                                :name="`phone.${index}`"
                                :title="$t('messages.only_numbers')"
                                v-model="phone.number"
                                required
                                pattern="[0-9]+"
                                class="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300"
                            />
                        </div>
                    </div>
                    <div class="sm:col-span-2">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $t('messages.label') }}
                        </label>
                        <div class="mt-1">
                            <input
                                type="text"
                                v-model="phone.label"
                                list="phone_labels"
                                class="w-full border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                        </div>
                    </div>
                    <div>
                        <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                        <div class="mt-1">
                            <button
                                type="button"
                                @click="deletePhone(index)"
                                class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                    <path
                                        fill-rule="evenodd"
                                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="flex flex-col mt-5">
                    <div>
                        <button
                            type="button"
                            @click="addPhone()"
                            class="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                                class="h-5 w-5"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </button>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.password') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.password')" for="password" />
                            <FormInput
                                id="password"
                                v-model="form.password"
                                name="password"
                                autocomplete="new-password"
                                type="password"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.password" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.password_repeat')" for="password_confirmation" />
                            <FormInput
                                id="password_confirmation"
                                v-model="form.password_confirmation"
                                name="password_confirmation"
                                autocomplete="new-password"
                                type="password"
                                max="191"
                                class="mt-1 w-full"
                            />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $tc('messages.students', 2) }}
                    <SearchStudentPanel @select-student="addStudent" />
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="flex flex-col mt-5 col-span-12">
                        <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                            <div
                                class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                            >
                                <table class="min-w-full divide-y divide-gray-200">
                                    <thead>
                                        <tr>
                                            <th
                                                class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                {{ $t('messages.name') }}
                                            </th>
                                            <th
                                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                {{ $t('messages.relationship') }}
                                            </th>
                                            <th
                                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                {{ $t('messages.can_take_student') }}
                                            </th>
                                            <th
                                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                <span class="sr-only">{{ $t('messages.actions') }}</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr class="bg-white" v-for="(student, index) in form.students" :key="index">
                                            <td
                                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                            >
                                                <div class="flex space-x-2">
                                                    <img
                                                        :src="student.avatar_or_default"
                                                        alt="profile photo"
                                                        class="relative z-30 inline-block h-10 w-10 rounded-full ring-2 ring-white"
                                                    />
                                                    <div>
                                                        <div>
                                                            {{ student.full_name }}
                                                        </div>
                                                        <div>
                                                            <BadgePill color="yellow" v-if="!student.active">
                                                                {{ $t('messages.inactive') }}
                                                            </BadgePill>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td
                                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                            >
                                                <FormSelect
                                                    required
                                                    :id="`relationship_${student.id}`"
                                                    v-model="student.relationship"
                                                    :options="options.relationship_types"
                                                    class="mt-1 w-full"
                                                />
                                            </td>
                                            <td
                                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                            >
                                                <div
                                                    v-if="student.can_take_student"
                                                    :title="$t('messages.can_take_student')"
                                                    class="inline-block"
                                                >
                                                    <svg
                                                        class="w-4 h-4 inline-block text-green-300"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                                            clip-rule="evenodd"
                                                        ></path>
                                                    </svg>
                                                    <svg
                                                        class="w-4 h-4 inline-block text-green-300"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
                                                        ></path>
                                                    </svg>
                                                </div>
                                                <div
                                                    v-else
                                                    :title="$t('messages.cannot_take_student')"
                                                    class="inline-block"
                                                >
                                                    <svg
                                                        class="w-4 h-4 inline-block text-red-300"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                                            clip-rule="evenodd"
                                                        ></path>
                                                    </svg>
                                                    <svg
                                                        class="w-4 h-4 inline-block text-red-300"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
                                                        ></path>
                                                    </svg>
                                                </div>
                                                <input
                                                    :id="`can_take_student_${student.id}`"
                                                    type="checkbox"
                                                    v-model="student.can_take_student"
                                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                />
                                            </td>
                                            <td
                                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                            >
                                                <button
                                                    type="button"
                                                    @click="deleteStudent(index)"
                                                    class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                >
                                                    <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                            clip-rule="evenodd"
                                                        ></path>
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5">
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import AdminLayout from '@/Shared/AdminLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import PhonePrefixSelector from '@/components/pageSpecific/PhonePrefixSelector.vue';
import FormSelectFilterablePanel from '@/components/Forms/FormSelectFilterablePanel.vue';
import SearchStudentPanel from '@/components/SearchStudentPanel.vue';
import BadgePill from '@/components/BadgePill.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        FormSelectFilterablePanel,
        PhonePrefixSelector,
        SearchStudentPanel,
        BadgePill,
    },
    layout: AdminLayout,
    name: 'Create',
    props: ['meta', 'options'],
    data() {
        return {
            form: this.$inertia.form({
                first_name: '',
                name: '',
                last_name: '',
                second_last_name: '',
                date_of_birth: '',
                email: '',
                gender: '',
                mother_tongue: 'es',
                nationality: 'ES',
                country_of_origin: 'ES',
                password: '',
                password_confirmation: '',
                preferred_language: '',
                identification_document: '',
                phones: [],
                students: this.options.students,
            }),
        };
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.store);
        },
        addPhone() {
            this.form.phones.push({
                dial_code: '34', //Spain
                number: '',
                label: '',
            });
        },
        deletePhone(index) {
            this.form.phones.splice(index, 1);
        },
        deleteStudent(index) {
            this.form.students.splice(index, 1);
        },
        addStudent(student) {
            this.form.students.push({
                id: student.id,
                full_name: student.full_name,
                active: student.active,
                avatar_or_default: student.avatar_or_default,
                relationship: '',
                can_take_student: true,
            });
        },
    },
    computed: {
        full_name: function () {
            if (!this.form.last_name && !this.form.second_last_name) {
                return '';
            }

            const lastNames = (this.form.last_name + ' ' + (this.form.second_last_name || '')).trim();
            const firstName = this.form.name ? this.form.name : this.form.first_name;
            return `${lastNames}, ${firstName}`;
        },
    },
};
</script>
